import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { api, API_BASE } from '../../api/client';
import ImageCropModal from '../../components/ImageCropModal';

interface Worker {
  id: number;
  fio: string;
  position: string;
  numberOffice: number | null;
  phoneNumber: string;
  mail: string;
  imageS3?: string | null;
}

const EMPTY = { fio: '', position: '', numberOffice: '', phoneNumber: '', mail: '' };

export default function AdminWorkersPage() {
  const [workers, setWorkers] = useState<Worker[]>([]);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [pendingImage, setPendingImage] = useState<File | null>(null);

  const load = () =>
    axios.get(`${API_BASE}/contacts-workers`).then(r => setWorkers(r.data));

  useEffect(() => { load(); }, []);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0] || null;
    if (f) setPendingImage(f);
    e.target.value = '';
  };

  const startEdit = (w: Worker) => {
    setEditId(w.id);
    setForm({
      fio: w.fio,
      position: w.position,
      numberOffice: String(w.numberOffice ?? ''),
      phoneNumber: w.phoneNumber,
      mail: w.mail,
    });
    setImageFile(null);
    setPendingImage(null);
  };

  const cancelEdit = () => { setEditId(null); setForm(EMPTY); setError(''); setImageFile(null); setPendingImage(null); };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const body = {
        fio: form.fio,
        position: form.position,
        numberOffice: form.numberOffice ? Number(form.numberOffice) : null,
        phoneNumber: form.phoneNumber,
        mail: form.mail,
      };
      let workerId = editId;
      if (editId) {
        await api.put(`/contacts-workers/${editId}`, body);
      } else {
        const res = await api.post('/contacts-workers', body);
        workerId = res.data.id;
      }

      if (imageFile && workerId) {
        const fd = new FormData();
        fd.append('file', imageFile);
        await api.post(`/contacts-workers/save-image/${workerId}`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }

      setForm(EMPTY);
      setEditId(null);
      setImageFile(null);
      setPendingImage(null);
      await load();
    } catch {
      setError('Ошибка при сохранении. Проверьте данные.');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id: number) => {
    if (!window.confirm('Удалить сотрудника?')) return;
    await api.delete(`/contacts-workers/${id}`);
    await load();
  };

  return (
    <div style={styles.page}>
      <h2 style={styles.title}>Сотрудники (страница Контакты)</h2>

      {/* Форма */}
      <div style={styles.card}>
        <h3 style={styles.cardTitle}>
          {editId ? '✏️ Редактировать сотрудника' : '➕ Добавить сотрудника'}
        </h3>
        <form onSubmit={handleSubmit} style={styles.form}>
          {error && <div style={styles.error}>{error}</div>}

          <div style={styles.row}>
            <div style={styles.field}>
              <label style={styles.label}>ФИО *</label>
              <input style={styles.input} placeholder="Иванов Иван Иванович"
                value={form.fio} onChange={e => setForm({ ...form, fio: e.target.value })} required />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Должность *</label>
              <input style={styles.input} placeholder="Председатель"
                value={form.position} onChange={e => setForm({ ...form, position: e.target.value })} required />
            </div>
          </div>

          <div style={styles.row}>
            <div style={styles.field}>
              <label style={styles.label}>Номер кабинета</label>
              <input style={styles.input} placeholder="101" type="number"
                value={form.numberOffice} onChange={e => setForm({ ...form, numberOffice: e.target.value })} />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Телефон *</label>
              <input style={styles.input} placeholder="+996 312 123456"
                value={form.phoneNumber} onChange={e => setForm({ ...form, phoneNumber: e.target.value })} required />
            </div>
          </div>

          <div style={styles.field}>
            <label style={styles.label}>Email *</label>
            <input style={styles.input} type="email" placeholder="ivanov@health.kg"
              value={form.mail} onChange={e => setForm({ ...form, mail: e.target.value })} required />
          </div>

          <div style={styles.field}>
            <label style={styles.label}>Фотография</label>
            <input
              type="file"
              accept="image/*"
              style={styles.input}
              onChange={handleImageChange}
            />
            {imageFile && (
              <span style={{ fontSize: '0.82rem', color: '#555' }}>Выбрано: {imageFile.name}</span>
            )}
          </div>

          <div style={{ display: 'flex', gap: 10 }}>
            <button style={styles.btn} type="submit" disabled={saving}>
              {saving ? 'Сохранение...' : editId ? '💾 Сохранить' : '➕ Добавить'}
            </button>
            {editId && (
              <button type="button" onClick={cancelEdit} style={styles.btnCancel}>
                Отмена
              </button>
            )}
          </div>
        </form>
      </div>

      {/* Список */}
      <div style={styles.card}>
        <h3 style={styles.cardTitle}>Список сотрудников ({workers.length})</h3>
        {workers.length === 0 ? (
          <p style={styles.empty}>Сотрудников пока нет</p>
        ) : (
          <div style={styles.tableWrapper}>
            <table style={styles.table}>
              <thead>
                <tr style={styles.thead}>
                  <th style={styles.th}>Фото</th>
                  <th style={styles.th}>ФИО</th>
                  <th style={styles.th}>Должность</th>
                  <th style={styles.th}>Каб.</th>
                  <th style={styles.th}>Телефон</th>
                  <th style={styles.th}>Email</th>
                  <th style={styles.th}>Действия</th>
                </tr>
              </thead>
              <tbody>
                {workers.map(w => (
                  <tr key={w.id} style={styles.tr}>
                    <td style={styles.td}>
                      {w.imageS3 ? (
                        <img
                          src={`${API_BASE}/contacts-workers/get-image/${w.imageS3}`}
                          alt={w.fio}
                          style={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover' }}
                        />
                      ) : (
                        <div style={{ width: 40, height: 40, borderRadius: '50%', background: '#1a3a6b', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700 }}>
                          {w.fio.charAt(0)}
                        </div>
                      )}
                    </td>
                    <td style={styles.td}>{w.fio}</td>
                    <td style={styles.td}>{w.position}</td>
                    <td style={styles.td}>{w.numberOffice ?? '—'}</td>
                    <td style={styles.td}>{w.phoneNumber}</td>
                    <td style={styles.td}>{w.mail}</td>
                    <td style={styles.td}>
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'nowrap' }}>
                        <button onClick={() => startEdit(w)} style={styles.btnEdit}>✏️</button>
                        <button onClick={() => remove(w.id)} style={styles.btnDanger}>🗑</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ImageCropModal
        file={pendingImage}
        aspect={1}
        round
        title="Обрезать фотографию сотрудника"
        onCancel={() => setPendingImage(null)}
        onCropped={cropped => { setImageFile(cropped); setPendingImage(null); }}
      />
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  page: { padding: 'clamp(16px, 4vw, 32px)', maxWidth: 1200, margin: '0 auto' },
  title: { color: '#1a3a6b', fontSize: 'clamp(1.3rem, 3vw, 1.6rem)', marginBottom: 'clamp(16px, 3vw, 24px)' },
  card: { background: '#fff', borderRadius: 10, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', marginBottom: 16, overflow: 'hidden' },
  cardTitle: { color: '#1a3a6b', fontSize: 'clamp(0.9rem, 2vw, 1rem)', padding: '16px 20px', borderBottom: '1px solid #f0f4fb' },
  form: { display: 'flex', flexDirection: 'column', gap: 12, padding: '16px 20px 20px' },
  row: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 250px), 1fr))', gap: 12 },
  field: { display: 'flex', flexDirection: 'column', gap: 4 },
  label: { fontSize: '0.85rem', fontWeight: 600, color: '#444' },
  input: { padding: '9px 12px', borderRadius: 8, border: '1px solid #dde3ef', fontSize: '0.9rem', outline: 'none', width: '100%' },
  btn: { padding: '9px 20px', background: '#1a3a6b', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 600, cursor: 'pointer' },
  btnCancel: { padding: '9px 20px', background: '#f0f4fb', color: '#555', border: 'none', borderRadius: 8, cursor: 'pointer' },
  error: { background: '#fff0f0', color: '#c0392b', padding: '10px 14px', borderRadius: 8, fontSize: '0.9rem' },
  empty: { padding: '16px 20px', color: '#888', fontSize: '0.9rem' },
  tableWrapper: { overflowX: 'auto', width: '100%' },
  table: { width: '100%', borderCollapse: 'collapse', minWidth: 800 },
  thead: { background: '#f0f4fb' },
  th: { padding: '10px 16px', textAlign: 'left', fontSize: '0.82rem', color: '#555', fontWeight: 600, whiteSpace: 'nowrap' },
  tr: { borderTop: '1px solid #f0f4fb' },
  td: { padding: '10px 16px', fontSize: '0.88rem', color: '#333', whiteSpace: 'nowrap' },
  btnEdit: { padding: '6px 10px', background: '#f39c12', color: '#fff', border: 'none', borderRadius: 5, cursor: 'pointer', fontSize: '0.9rem' },
  btnDanger: { padding: '6px 10px', background: '#e74c3c', color: '#fff', border: 'none', borderRadius: 5, cursor: 'pointer', fontSize: '0.9rem' },
};
